import React, { useState } from 'react';
import styled from 'styled-components';

import {
  GrowthPlannerHandoffBlock,
  GrowthPlannerMetricOption,
  ResponseTone,
  SupportAction,
  SupportActionIcon
} from '../../types';
import { ResponseActionButton, ResponseIcon } from '../ResponseActionButton';
import { useResponseAction } from '../responseActions';
import { ResponseReply } from '../responsePrimitives';
import { responseTone, responseType, rsColor, rsFor, rsHandoffColor, rsv } from '../responseScale';
import HandoffChecks from './handoff/HandoffChecks';
import HandoffMetrics from './handoff/HandoffMetrics';
import HandoffResult from './handoff/HandoffResult';

/** 6473:12861: the summary note's glyph per tone; a tone without one draws the text alone. */
const NOTE_GLYPH: Partial<Record<ResponseTone, SupportActionIcon>> = {
  warning: 'warning',
  error: 'warning',
  success: 'check'
};

// 6473:12314: the chooser's column, plus the 7 it sits right of the status line (6473:12804).
const Reply = styled(ResponseReply)`
  max-width: ${props => rsFor(props).size.handoffColumn + rsFor(props).space.chooserInset}px;
`;

// 6473:12803: Semi Bold 12, white; its top level with the reply mark's, which sits 4 down.
const StatusLine = styled.p`
  margin: ${rsv.space('statusLineTop')}px 0 0;
  color: ${rsColor.white};
  ${responseType('statusLine')}
`;

const Chooser = styled.div`
  padding-left: ${rsv.space('chooserInset')}px;

  ${StatusLine} + & {
    margin-top: ${rsv.space('chooserTop')}px;
  }
`;

// 6473:12854: a #E4E8F0 rule over the selection summary and its note.
const Summary = styled.div`
  margin-top: ${rsv.space('summaryTop')}px;
  padding: ${rsv.space('summary')};
  border-top: 1px solid ${rsHandoffColor.cardRule};
  color: ${rsColor.secondary};
  ${responseType('handoffBody')}
`;

// 6473:12855: the counts spread across one metric column.
const SummaryItems = styled.ul`
  display: flex;
  justify-content: space-between;
  max-width: ${rsv.size('metricCardWidth')}px;
  margin: 0;
  padding: 0;
  list-style: none;
`;

// 6473:12858: the glyph 14, 5 before the text.
const Note = styled.p`
  display: flex;
  align-items: center;
  margin: ${rsv.space('noteTop')}px 0 0;

  .anticon {
    font-size: ${rsv.size('noteIcon')}px;
  }

  > * + * {
    margin-left: ${rsv.space('noteIconGap')}px;
  }
`;

const Footer = styled.div`
  margin-top: ${rsv.space('continueTop')}px;

  > * + * {
    margin-top: ${rsv.space('continueGap')}px;
  }
`;

// 6473:12859: 960 x 43, r10, Bold 16, centred; greyed #98A5BF once the hand-off has started.
const ContinueButton = styled(ResponseActionButton)`
  && {
    justify-content: center;
    width: 100%;
    padding: ${rsv.space('continueButton')};
    ${responseType('continue')}
  }
`;

/** The options the payload marks picked; an unavailable option is never one. */
const preselected = (options: GrowthPlannerMetricOption[]): string[] =>
  options.filter(option => option.available && option.selected).map(option => option.id);

/** Figma 6473:11977 (preparing), 6473:12314 (choosing), 6473:12607 / 12872 / 13248 (sending, sent, failed). */
const GrowthPlannerHandoff: React.FC<{ block: GrowthPlannerHandoffBlock }> = ({ block }) => {
  const emit = useResponseAction(block);
  const options = block.metrics?.options ?? [];
  const actions = block.actions ?? [];
  // `null` until the user picks: the payload's selection stands, also when a later payload sends it.
  const [picked, setPicked] = useState<string[] | null>(null);
  // Once the hand-off has started, the payload's selection stands and Continue stays greyed.
  const locked = block.status !== 'choosing';
  const selected = locked || picked === null ? preselected(options) : picked;
  const metricIds = options
    .filter(option => option.available && selected.includes(option.id))
    .map(option => option.id);

  const toggle = (id: string) =>
    setPicked(current => {
      const base = current ?? preselected(options);
      return base.includes(id) ? base.filter(other => other !== id) : [...base, id];
    });
  const onAction = (action: SupportAction) => emit(action, { metric_ids: metricIds });

  const { metrics, checks, summary, result } = block;
  const note = summary?.note;
  const noteGlyph = note ? NOTE_GLYPH[note.tone] : undefined;
  const hasChooser = Boolean(metrics || checks || summary || actions.length || result);

  return (
    <Reply
      data-testid="orbit-response-growth_planner_handoff"
      data-block-id={block.id}
      data-status={block.status}
    >
      {block.status_line ? (
        <StatusLine data-testid="orbit-handoff-status-line">{block.status_line}</StatusLine>
      ) : null}
      {hasChooser ? (
        <Chooser>
          {metrics ? (
            <HandoffMetrics
              heading={metrics.heading}
              options={options}
              selected={selected}
              locked={locked}
              onToggle={toggle}
            />
          ) : null}
          {checks ? <HandoffChecks heading={checks.heading} rows={checks.rows} /> : null}
          {summary ? (
            <Summary data-testid="orbit-handoff-summary">
              <SummaryItems>
                {summary.items.map(item => (
                  <li key={item}>{item}</li>
                ))}
              </SummaryItems>
              {note ? (
                <Note>
                  {noteGlyph ? (
                    <span style={{ color: responseTone(note.tone).ink }}>
                      <ResponseIcon icon={noteGlyph} />
                    </span>
                  ) : null}
                  <span>{note.text}</span>
                </Note>
              ) : null}
            </Summary>
          ) : null}
          {actions.length ? (
            <Footer>
              {actions.map(action => (
                <ContinueButton
                  key={action.id}
                  action={action}
                  onAction={onAction}
                  disabled={locked}
                />
              ))}
            </Footer>
          ) : null}
          {result ? (
            <HandoffResult status={block.status} result={result} onAction={onAction} />
          ) : null}
        </Chooser>
      ) : null}
    </Reply>
  );
};

export default GrowthPlannerHandoff;
